import { useCallback, useEffect, useMemo, useState } from "react";
import { GitCompareArrows, X } from "lucide-react";
import { TelemetryUpload } from "@/components/telemetry/TelemetryUpload";
import { LapFileUpload } from "@/components/telemetry/LapFileUpload";
import { TelemetryLapTable } from "@/components/telemetry/TelemetryLapTable";
import { TelemetryAnalysis, chip } from "@/components/telemetry/TelemetryAnalysis";
import { LapPicker, useCatalog, useRemoteLap } from "@/components/telemetry/LapPicker";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { fullLaps, lapNumbers } from "@/lib/telemetry";

const ExternalB = ({ trackId, telId, value, onChange, onClose }) => {
  const { t } = useT();
  const items = useCatalog(trackId);
  const others = (items || []).filter((i) => i.id !== telId);
  return (
    <div data-testid="external-b-panel" className="bg-[#12141C] border border-[#282D3D] rounded-sm p-3 flex items-end gap-3">
      <div className="flex-1 min-w-0">
        {others.length ? <LapPicker items={others} value={value} onChange={onChange} testid="external-b-picker" color="#FF3B30" label={t("lap_b_other_session")} />
          : <p data-testid="external-b-empty" className="font-mono text-xs text-slate-500">{trackId ? t("no_other_sessions_track") : t("track_not_detected")}</p>}
      </div>
      <button type="button" data-testid="external-b-close" onClick={onClose} className="p-2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
};

const TelemetryView = ({ tel, sessionId, trackId, setForm, rev }) => {
  const { t } = useT();
  const ranked = useMemo(() => fullLaps(tel), [tel]);
  const [a, setA] = useState(ranked[0]?.lap ?? null);
  const [b, setB] = useState(ranked[1]?.lap ?? null);
  const [c, setC] = useState(null);
  const [laps, setLaps] = useState({});
  const [extOpen, setExtOpen] = useState(false);
  const [ext, setExt] = useState(null);
  const extItems = useCatalog(extOpen ? trackId : undefined);
  const extLap = useRemoteLap(extOpen ? ext : null, extItems);
  const num = useMemo(() => lapNumbers(tel), [tel]);

  useEffect(() => {
    const want = [a, b, c].filter((n) => n != null);
    if (!want.length) {
      return;
    }
    api.get(`/telemetry/${tel.id}/laps`, { params: { laps: want.join(",") } })
      .then((r) => setLaps(Object.fromEntries(r.data.map((l) => [l.lap, { ...l, lap: num[l.lap] ?? l.lap }]))));
  }, [tel.id, a, b, c, num, rev]);

  const lapA = laps[a];
  const lapB = extOpen ? extLap : (b != null ? laps[b] ?? null : null);
  const lapC = c != null ? laps[c] ?? null : null;
  const closeExt = () => { setExtOpen(false); setExt(null); };

  return (
    <div className="space-y-4">
      <TelemetryLapTable tel={tel} best={ranked[0]} a={a} b={extOpen ? null : b} c={c} setA={setA} setB={(v) => { closeExt(); setB(v); }} setC={setC} num={num} />
      {extOpen ? <ExternalB trackId={trackId} telId={tel.id} value={ext} onChange={setExt} onClose={closeExt} /> : (
        <button type="button" data-testid="compare-other-session-btn" onClick={() => setExtOpen(true)} className={`flex items-center gap-1.5 ${chip(false)}`}>
          <GitCompareArrows className="w-3.5 h-3.5" /> {t("compare_other_session")}
        </button>
      )}
      {lapA && <TelemetryAnalysis lapA={lapA} lapB={lapB} lapC={lapC} channels={tel.channels} trackId={trackId} mapKey={tel.id} sessionId={sessionId} setForm={setForm} csvTrack={tel.meta?.Track} />}
    </div>
  );
};

export const TelemetryTab = ({ sessionId, pit, setForm, trackId, lapData, lapData2 }) => {
  const { t } = useT();
  const [stint, setStint] = useState(1);
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get(`/sessions/${sessionId}/telemetry`).then((r) => {
      setItems(r.data);
      const tid = r.data[0]?.track_id;
      if (tid) {
        setForm((f) => (f.track_id === tid ? f : { ...f, track_id: tid }));
      }
    });
  }, [sessionId, setForm]);
  useEffect(() => {
    if (sessionId) {
      load();
    }
  }, [sessionId, load]);

  if (!sessionId) return <p data-testid="telemetry-save-first" className="font-mono text-sm text-[#FFB800]">{t("telemetry_save_first")}</p>;
  const current = items.find((i) => i.stint === (pit ? stint : 1));
  const onChange = (res) => {
    load();
    if (res?.session_update && Object.keys(res.session_update).length) {
      setForm((f) => ({ ...f, ...res.session_update }));
    }
  };
  const n = pit ? stint : 1;
  const ld = n === 1 ? lapData : lapData2;
  return (
    <div className="space-y-5">
      {pit && (
        <div className="flex gap-2">
          {[1, 2].map((k) => <button type="button" key={k} data-testid={`telemetry-stint-${k}`} onClick={() => setStint(k)} className={chip(stint === k)}>PIT · {t(`stint${k}`)}</button>)}
        </div>
      )}
      <TelemetryUpload sessionId={sessionId} stint={n} current={current} onChange={onChange} />
      <LapFileUpload sessionId={sessionId} stint={n} lapData={ld} onChange={(upd) => setForm((f) => ({ ...f, ...upd }))} />
      {current && <TelemetryView key={current.id} tel={current} sessionId={sessionId} trackId={trackId} setForm={setForm} rev={ld?.imported_at} />}
    </div>
  );
};
